import React, { useEffect, useState, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { goodsReceiptsApi } from "../../../api/inventory/goods_receipts";
import { purchasesApi } from "../../../api/inventory/purchases";
import { inventoryStoragesApi } from "../../../api/inventory_storages";
import { ResourceLoading } from "../../../components/utils/ResourceLoading";
import { ResourceError } from "../../../components/utils/ResourceError";
import DashboardHeader from "../../../components/dashboard/dashboardHeader";
import {
  ArrowLeftIcon,
  PlusIcon,
  TrashIcon,
  CheckCircleIcon,
} from "@heroicons/react/24/outline";
import { Button, TextField, MenuItem, Select, FormControl } from "@mui/material";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import toast from "react-hot-toast";
import ModernProductSelectorDialog from "../../../components/utils/Selector/ModernProductSelectorDialog";
import { buildGoodsReceiptPayload } from "./goods-receipt-payload";
import { isValidInventoryQuantity } from "./inventory-quantity";
import { osloToday, makeInventoryRequestKey } from "./inventory-format";
import { ReceiptSyncStatus } from "../../../components/dashboard/inventory/ReceiptSyncStatus";
import { normalizeError } from "../../../operationFeedback/normalizeError";

export const GoodsReceipt = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const companyId = useSelector((state) => state.session.currentCompany?.id);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const purchaseId = searchParams.get("purchase_id");
  const isNew = !id || id === "new";
  
  const [goodsReceipt, setGoodsReceipt] = useState(null);
  const [purchase, setPurchase] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [storages, setStorages] = useState([]);
  const [storageError, setStorageError] = useState(false);
  const [addProductDialogOpen, setAddProductDialogOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [unknownOutcome, setUnknownOutcome] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const pendingPayload = useRef(null);
  const requestKey = useRef(makeInventoryRequestKey());
  const [removedItemIds, setRemovedItemIds] = useState([]);
  const pendingStorageKey = `promang:receipt-pending:${companyId || "none"}:${purchaseId || "none"}:${id || "new"}`;
  const isPosted = !!goodsReceipt?.posted_at || ["partial", "completed", "cancelled"].includes(goodsReceipt?.status);

  const [formData, setFormData] = useState({
    receipt_date: osloToday(),
    comment: "",
    purchase_id: purchaseId || null,
    items: [],
  });
  const canAddItems = !purchaseId && !formData.purchase_id && !isPosted && !unknownOutcome && !saving;

  useEffect(() => {
    try {
      const saved = sessionStorage.getItem(pendingStorageKey);
      if (!saved) return;
      const pending = JSON.parse(saved);
      if (!pending?.payload?.request_key || !pending?.formData) return;
      pendingPayload.current = pending.payload;
      requestKey.current = pending.payload.request_key;
      setFormData(pending.formData);
      setUnknownOutcome(true);
    } catch (_) { /* A corrupt browser draft must not bypass server validation. */ }
  }, [pendingStorageKey]);

  useEffect(() => {
    if (isNew) {
      fetchStorages();
      if (purchaseId) {
        fetchPurchase();
      }
      setLoading(false);
    } else {
      fetchGoodsReceipt();
      fetchStorages();
    }
  }, [id, purchaseId, isNew]);

  const fetchStorages = async () => {
    try {
      const response = await inventoryStoragesApi.getInventoryStorages({ active: true });
      if (response?.success) {
        setStorages(response.data || []);
        setStorageError(false);
      } else if (Array.isArray(response)) {
        setStorages(response);
        setStorageError(false);
      }
    } catch (_) { setStorageError(true); }
  };

  const fetchPurchase = async () => {
    try {
      const response = await purchasesApi.getPurchase(purchaseId);
      if (response?.data?.success) {
        const purchaseData = response.data.data;
        setPurchase(purchaseData);
        
        // Pre-fill items from purchase
        if (purchaseData.items && purchaseData.items.length > 0) {
          const items = purchaseData.items.filter((item) => Number(item.remaining_quantity ?? item.quantity) > 0).map((item) => ({
            purchase_item_id: item.id,
            product_id: item.product_id,
            product: item.product,
            quantity_expected: item.remaining_quantity ?? item.quantity,
            quantity_received: 0,
            remaining_quantity: item.remaining_quantity ?? item.quantity,
            storage_id: null,
            unit_id: item.unit_id,
            comment: "",
          }));
          if (!pendingPayload.current) setFormData((prev) => ({ ...prev, items }));
        }
      }
    } catch (_) {
      toast.error("Kunne ikke hente innkjøp");
    }
  };

  const fetchGoodsReceipt = async () => {
    try {
      setLoading(true);
      const response = await goodsReceiptsApi.getGoodsReceipt(id);
      if (response?.data?.success) {
        const receiptData = response.data.data;
        setGoodsReceipt(receiptData);
        if (!pendingPayload.current) setFormData({
          receipt_date: receiptData.receipt_date ? String(receiptData.receipt_date).slice(0, 10) : osloToday(),
          comment: receiptData.comment || "",
          purchase_id: receiptData.purchase_id || null,
          items: receiptData.items || [],
        });
        if (receiptData.purchase_id) {
          fetchPurchaseForReceipt(receiptData.purchase_id);
        }
      } else {
        setError("Kunne ikke hente varemottak");
      }
    } catch (_) {
      setError("Kunne ikke hente varemottak");
    } finally {
      setLoading(false);
    }
  };

  const fetchPurchaseForReceipt = async (purchaseId) => {
    try {
      const response = await purchasesApi.getPurchase(purchaseId);
      if (response?.data?.success) {
        setPurchase(response.data.data);
      }
    } catch (_) { toast.error("Kunne ikke hente innkjøp"); }
  };

  const handleSave = async () => {
    if (saving) return;
    if (isPosted) {
      toast.error(t("inventoryReceipt.postedImmutable")); return;
    }
    if (!formData.receipt_date) {
      toast.error("Mottaksdato er påkrevd");
      return;
    }

    if (!formData.items || formData.items.length === 0) {
      toast.error("Legg til minst ett produkt");
      return;
    }

    // Validate all items have product and storage
    for (const item of formData.items) {
      if (!item.product_id) {
        toast.error("Alle produkter må ha et produkt valgt");
        return;
      }
      if (!isValidInventoryQuantity(item.quantity_received, { allowZero: true })) {
        toast.error(t("inventoryReceipt.quantityInvalid"));
        return;
      }
      if (Number(item.quantity_received) > 0 && !item.storage_id) {
        toast.error("Alle produkter må ha et lager valgt");
        return;
      }
    }

    const data = pendingPayload.current || buildGoodsReceiptPayload(formData, removedItemIds, requestKey.current);
    if (isNew && !data.items_attributes.some((item) => !item._destroy)) {
      toast.error(t("inventoryReceipt.addProduct"));
      return;
    }
    setSaving(true);
    setSaveError(null);
    try { sessionStorage.setItem(pendingStorageKey, JSON.stringify({ payload: data, formData })); } catch (_) { /* In-memory retry still works. */ }
    try {
      if (isNew) {
        const response = await goodsReceiptsApi.createGoodsReceipt(data);

        if (response?.data?.success) {
          toast.success("Varemottak opprettet");
          pendingPayload.current = null;
          setUnknownOutcome(false);
          try { sessionStorage.removeItem(pendingStorageKey); } catch (_) { /* In-memory state remains cleared. */ }
          navigate(`/dashboard/inventory/goods-receipts/${response.data.data.id}`);
        } else {
          pendingPayload.current = data;
          setUnknownOutcome(true);
        }
      } else {
        const response = await goodsReceiptsApi.updateGoodsReceipt(id, data);

        if (response?.data?.success) {
          toast.success("Varemottak oppdatert");
          pendingPayload.current = null;
          setUnknownOutcome(false);
          setRemovedItemIds([]);
          requestKey.current = makeInventoryRequestKey();
          try { sessionStorage.removeItem(pendingStorageKey); } catch (_) { /* In-memory state remains cleared. */ }
          fetchGoodsReceipt();
        } else {
          pendingPayload.current = data;
          setUnknownOutcome(true);
        }
      }
    } catch (error) {
      const failure = normalizeError(error);
      // This endpoint rolls back validation failures. A retry after an unknown
      // outcome cannot prove the ORIGINAL attempt failed, even on 409/422.
      if (!pendingPayload.current && failure.httpStatus === 422 &&
          error?.response?.data?.success === false && failure.outcome === "not_applied" &&
          failure.code !== "request_key_reused") {
        setSaveError(failure.safeMessage || t("inventoryReceipt.saveFailed"));
        requestKey.current = makeInventoryRequestKey();
        pendingPayload.current = null;
        setUnknownOutcome(false);
        try { sessionStorage.removeItem(pendingStorageKey); } catch (_) { /* In-memory state remains cleared. */ }
      } else {
        pendingPayload.current = data;
        setUnknownOutcome(true);
      }
    } finally { setSaving(false); }
  };

  const handleAddItem = () => {
    if (!canAddItems) return;
    setAddProductDialogOpen(true);
  };

  const handleProductSelected = (products) => {
    if (!canAddItems) return;
    setFormData((prev) => {
      const known = new Set((prev.items || []).map((item) => String(item.product_id)));
      const added = products.filter((product) => !known.has(String(product.id))).map((product) => ({
        product_id: product.id, product,
        quantity_expected: 0, quantity_received: 0,
        storage_id: null, unit_id: product.unit_id || null, comment: "",
      }));
      return { ...prev, items: [...prev.items, ...added] };
    });
  };


  const handleRemoveItem = (index) => {
    if (!confirm("Er du sikker på at du vil fjerne denne linjen?")) return;
    if (formData.items[index]?.id) setRemovedItemIds((ids) => [...ids, formData.items[index].id]);
    const updatedItems = formData.items.filter((_, i) => i !== index);
    setFormData((prev) => ({ ...prev, items: updatedItems }));
  };

  if (loading) {
    return (
      <>
        <DashboardHeader title={isNew ? "Nytt varemottak" : "Varemottak"} subtitle="Laster..." />
        <div className="mx-auto max-w-7xl px-3 sm:px-6 lg:px-8">
          <ResourceLoading />
        </div>
      </>
    );
  }

  if (error && !isNew) {
    return (
      <>
        <DashboardHeader title="Varemottak" subtitle="" />
        <div className="mx-auto max-w-7xl px-3 sm:px-6 lg:px-8">
          <ResourceError errors={[error]} />
        </div>
      </>
    );
  }

  return (
    <>
      <DashboardHeader
        title={isNew ? "Nytt varemottak" : `Varemottak ${goodsReceipt?.receipt_number || ""}`}
        subtitle={purchase ? `Fra innkjøp ${purchase.purchase_number}` : ""}
        actions={[
          {
            component: null,
            label: "Tilbake",
            description: "",
            path: purchaseId ? `/dashboard/inventory/purchases/${purchaseId}` : "/dashboard/inventory/purchases",
            action: null,
            icon: ArrowLeftIcon,
            textColor: "text-gray-700",
            bgColor: "bg-white",
            hidden: false,
            menu: false,
          },
        ]}
      />
      <div className="mx-auto max-w-7xl px-3 sm:px-6 lg:px-8 pb-24">
        <div className="mt-8 space-y-6">
          {(unknownOutcome || saveError) && <div role="alert" className="pm-alert pm-alert--warn">
            {unknownOutcome ? t("inventoryReceipt.unknownOutcome") : saveError}
          </div>}
          {!!goodsReceipt?.posted_at && <ReceiptSyncStatus key={`${companyId}:${id}`} receipt={goodsReceipt} onRefresh={fetchGoodsReceipt} />}
          {storageError && <div role="alert" className="rounded-xl border border-rose-200 bg-rose-50 p-4 text-sm text-rose-800">{t("inventoryReceipt.storageLoadFailed")} <button type="button" className="font-semibold underline" onClick={fetchStorages}>{t("inventoryValuation.refresh")}</button></div>}
          {/* Form Details */}
          <div className="bg-white shadow rounded-lg p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Mottaksdato *</label>
                <TextField
                  fullWidth
                  type="date"
                  variant="outlined"
                  size="small"
                  value={formData.receipt_date}
                  onChange={(e) => setFormData({ ...formData, receipt_date: e.target.value })}
                  disabled={isPosted || unknownOutcome || saving}
                  InputLabelProps={{ shrink: true }}
                />
              </div>
              {purchase && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Innkjøp</label>
                  <TextField
                    fullWidth
                    variant="outlined"
                    size="small"
                    value={purchase.purchase_number || ""}
                    disabled
                  />
                </div>
              )}
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">Kommentar</label>
                <TextField
                  fullWidth
                  multiline
                  rows={3}
                  variant="outlined"
                  size="small"
                  value={formData.comment || ""}
                  onChange={(e) => setFormData({ ...formData, comment: e.target.value })}
                  disabled={isPosted || unknownOutcome || saving}
                />
              </div>
            </div>
          </div>

          {/* Items */}
          <div className="bg-white shadow rounded-lg p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-gray-900">Mottatte produkter</h3>
              <Button
                variant="contained"
                startIcon={<PlusIcon className="h-4 w-4" />}
                onClick={handleAddItem}
                disabled={!canAddItems}
              >
                Legg til produkt
              </Button>
            </div>

            {formData.items && formData.items.length > 0 ? (
              <Table sx={{ minWidth: 650 }}>
                <TableHead>
                  <TableRow>
                    <TableCell>Produkt</TableCell>
                    <TableCell>Forventet</TableCell>
                    <TableCell>Mottatt</TableCell>
                    <TableCell>Lager</TableCell>
                    <TableCell>Kommentar</TableCell>
                    <TableCell align="right">Handlinger</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {formData.items.map((item, index) => (
                    <TableRow key={item.id || item.purchase_item_id || index}>
                      <TableCell>
                        {item.product?.displayTitle || item.product?.title || item.title || "-"}
                      </TableCell>
                      <TableCell><span>{item.quantity_expected || 0}</span>{item.remaining_quantity !== undefined && <span className="block text-xs text-slate-500">{t("inventoryReceipt.remaining")}: {item.remaining_quantity}</span>}</TableCell>
                      <TableCell>
                        <TextField
                          size="small"
                          type="number"
                          value={item.quantity_received ?? ""}
                          onChange={(e) => {
                            const updated = [...formData.items];
                            updated[index] = { ...item, quantity_received: e.target.value };
                            setFormData((prev) => ({ ...prev, items: updated }));
                          }}
                          inputProps={{ min: 0, step: 0.0001 }}
                          sx={{ width: 100 }}
                          disabled={isPosted || unknownOutcome || saving}
                        />
                        {item.remaining_quantity !== undefined && !isPosted && <button type="button" className="block min-h-[40px] text-xs font-semibold text-[#1463C7] hover:underline" disabled={unknownOutcome || saving} onClick={() => setFormData((prev) => ({ ...prev, items: prev.items.map((row, rowIndex) => rowIndex === index ? { ...row, quantity_received: row.remaining_quantity } : row) }))}>{t("inventoryReceipt.receiveRemaining")}</button>}
                      </TableCell>
                      <TableCell>
                        <FormControl size="small" sx={{ minWidth: 150 }}>
                          <Select
                            value={item.storage_id || ""}
                            onChange={(e) => {
                              const updated = [...formData.items];
                              updated[index] = { ...item, storage_id: e.target.value || null };
                              setFormData((prev) => ({ ...prev, items: updated }));
                            }}
                            displayEmpty
                            disabled={isPosted || unknownOutcome || saving}
                          >
                            <MenuItem value="">Velg lager</MenuItem>
                            {storages.map((storage) => (
                              <MenuItem key={storage.id} value={storage.id}>
                                {storage.title}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </TableCell>
                      <TableCell>
                        <TextField
                          size="small"
                          value={item.comment || ""}
                          onChange={(e) => {
                            const updated = [...formData.items];
                            updated[index] = { ...item, comment: e.target.value };
                            setFormData((prev) => ({ ...prev, items: updated }));
                          }}
                          placeholder="Kommentar"
                          sx={{ width: 150 }}
                          disabled={isPosted || unknownOutcome || saving}
                        />
                      </TableCell>
                      <TableCell align="right">
                        <button
                          onClick={() => handleRemoveItem(index)}
                          className="text-red-600 hover:text-red-900"
                          title="Slett"
                          disabled={isPosted || unknownOutcome || saving}
                        >
                          <TrashIcon className="h-4 w-4" />
                        </button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            ) : (
              <div className="text-center py-12">
                <p className="text-gray-500 mb-4">Ingen produkter lagt til ennå</p>
                <Button variant="contained" onClick={handleAddItem} disabled={!canAddItems}>
                  Legg til første produkt
                </Button>
              </div>
            )}
          </div>

          {/* Save Button */}
          <div className="flex justify-end">
            <Button
              variant="contained"
              size="large"
              startIcon={<CheckCircleIcon className="h-5 w-5" />}
              onClick={handleSave}
              disabled={saving || isPosted}
            >
              {saving ? t("inventoryReceipt.saving") : unknownOutcome ? t("inventoryReceipt.retrySame") : isNew ? "Opprett varemottak" : "Lagre endringer"}
            </Button>
          </div>
        </div>
      </div>

      {/* Add Product Dialog */}
      <ModernProductSelectorDialog
        open={addProductDialogOpen}
        onClose={() => setAddProductDialogOpen(false)}
        onChange={handleProductSelected}
        selectedProducts={formData.items.map((item) => item.product).filter(Boolean)}
        multiple
      />
    </>
  );
};

